import React, { useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import { EyeIcon, Flex, IconButton, useDisclosure } from '@hivewatch/dark'

import {
  IncidentNoteMediaIProps,
  IncidentNotesMediaType,
} from '../../../shared/types/feedTypes'
import { IncidentNoteVideoPlayer } from '../../components/IncidentNoteVideoPlayer'
import { MediaPreviewerModal } from '../../modals/MediaPreviewerModal'
import { RenderMediaFile } from './RenderMediaFile'

const isVideo = (file: IncidentNotesMediaType) =>
  !!file.mimeType?.startsWith('video/')

const isImageOrPdf = (file: IncidentNotesMediaType) =>
  !!file.mimeType?.startsWith('image/') || file.mimeType === 'application/pdf'

export const isPreviewableIncidentNoteMedia = (file: IncidentNotesMediaType) =>
  isVideo(file) || isImageOrPdf(file)

export const MediaGrid = ({ files }: IncidentNoteMediaIProps) => {
  const { onClose, onOpen, open: isOpen } = useDisclosure()
  const [selectedFile, setSelectedFile] = useState(null)
  const colorScheme = useLightMode()

  const handleFileClick = (file: IncidentNotesMediaType) => {
    onOpen()
    setSelectedFile(file)
  }

  const videos = files.filter(isVideo)
  const imagesAndPDFs = files.filter(isImageOrPdf)
  const attachments = files.filter(
    (file) => !isPreviewableIncidentNoteMedia(file)
  )

  return (
    <Flex direction='column'>
      {videos.length > 0 && (
        <Flex
          alignItems='flex-start'
          flexWrap='wrap'
          gap='10px'
          justifyContent='space-between'
        >
          {videos.map((video) => (
            // Keyed on the media id, not the URL — polling re-signs the URL and
            // a changed key would remount the player mid-playback
            <Flex key={video.id} position='relative'>
              <IncidentNoteVideoPlayer videoUrl={video.url} />
              <IconButton
                aria-label='Expand'
                colorScheme={colorScheme}
                icon={<EyeIcon size={16} />}
                onClick={() => handleFileClick(video)}
                position='absolute'
                right='10px'
                top='10px'
                zIndex='1'
              />
            </Flex>
          ))}
        </Flex>
      )}
      <Flex flexWrap='wrap' gap='10px' justifyContent='space-between' mt='10px'>
        {[...imagesAndPDFs, ...attachments].map((file) => (
          <RenderMediaFile
            file={file}
            key={file.id ?? file.url}
            onFileClick={handleFileClick}
          />
        ))}
      </Flex>
      {selectedFile && isOpen && (
        <MediaPreviewerModal
          file={selectedFile}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
    </Flex>
  )
}
